import {
  CLOSED_KEYWORD,
  DAY_OF_WEEK_SCHEMA_MAP,
} from '@/features/seoPlaceDetail/lib/businessHours'

export interface IOpeningHoursSpecification {
  '@type': 'OpeningHoursSpecification'
  dayOfWeek: string | string[]
  opens: string
  closes: string
}

const TIME_RANGE_SEPARATOR = '~'

// "오전 9:00" / "오후 6:00" 형태의 12시간제 한글 표기를 "HH:MM"으로 정규화한다.
// 데이터 오염 케이스(예: "오후 13:00")는 이미 24시간제이므로 그대로 둔다.
const parseKoreanTime = (raw: string): string | null => {
  const matched = raw.trim().match(/^(오전|오후)\s*(\d{1,2}):(\d{2})$/)
  if (!matched) return null

  const [, meridiem, hourText, minuteText] = matched
  let hour = Number.parseInt(hourText, 10)
  const minute = Number.parseInt(minuteText, 10)
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return null
  if (!Number.isFinite(minute) || minute > 59) return null

  if (meridiem === '오전') {
    if (hour === 12) hour = 0 // 오전 12시 = 자정
  } else if (hour < 12) {
    hour += 12 // 오후 1~11시 → 13~23시 (오후 12시 정오·13시 이상 오염값은 그대로)
  }

  return `${String(hour).padStart(2, '0')}:${minuteText}`
}

const parseTimeRange = (
  value: string
): { opens: string; closes: string } | null => {
  const parts = value.split(TIME_RANGE_SEPARATOR)
  if (parts.length !== 2) return null

  const opens = parseKoreanTime(parts[0])
  const closes = parseKoreanTime(parts[1])
  if (!opens || !closes) return null

  // 종료가 자정(00:00)이면 자정 마감(24:00)으로 본다. 그 외 opens >= closes는 역전·자정 넘김으로 보고 제외한다.
  const normalizedCloses = closes === '00:00' ? '24:00' : closes
  if (opens >= normalizedCloses) return null

  return { opens, closes: normalizedCloses }
}

export const buildOpeningHoursSpecification = (
  businessHours: Record<string, string> | null | undefined
): IOpeningHoursSpecification[] => {
  if (!businessHours) return []

  const specs: IOpeningHoursSpecification[] = []
  const claimedDays = new Set<string>()

  for (const [day, value] of Object.entries(businessHours)) {
    const dayOfWeek = DAY_OF_WEEK_SCHEMA_MAP[day]
    if (!dayOfWeek) continue // 점심시간·휴무일 등 영업시간이 아닌 키는 제외

    const trimmedValue = value.trim()
    if (!trimmedValue || trimmedValue.includes(CLOSED_KEYWORD)) continue // 빈값·휴무 표기는 영업시간 없음

    const range = parseTimeRange(trimmedValue)
    if (!range) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[buildOpeningHoursSpecification] 영업시간 파싱 실패', {
          day,
          value,
        })
      }
      continue
    }

    // 평일+개별 요일 동시 존재 시 같은 요일이 두 spec에 중복되지 않도록 방지
    const days = Array.isArray(dayOfWeek) ? dayOfWeek : [dayOfWeek]
    if (days.some(d => claimedDays.has(d))) continue
    days.forEach(d => claimedDays.add(d))

    specs.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek,
      opens: range.opens,
      closes: range.closes,
    })
  }

  return specs
}
